import { yupResolver } from "@hookform/resolvers/yup";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Alert, Button, Grid, Tab } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import {
  BucketAwtENUM,
  EstadosActivacionInstallOTEnumChoice,
  EstadosOrdenTrabajoEnumChoice,
  gridSizeMdLg12,
  ROUTER_PATHS,
  useTabsFormOnly,
  useUploadFile,
} from "@/shared";
import {
  OrdenTrabajoQueryKeysEnum,
  useFetchUbicacionProductosEnabled,
  useUpdateOrdenTrabajo,
} from "@/shared/actions/erp";
import { guardarObservacionesTecnico } from "./observaciones-tecnico.actions";
import { calcularHistorialCambiosCoordenadas } from "./instalacion/utils/construirPayloadOrdenTrabajo";
import { usePreciosConceptosVisita } from "@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/usePreciosConceptosVisita";
import type { PuntoRed } from "@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/PuntosDeRedVisita";
import type { ConceptoVisita } from "@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/OtrosConceptosVisita";
import { useTiposInstalacion, trazaTipoInstalacion } from "@/erp/orden-servicio/orden-servicio-visita/components/tipo-instalacion/useTiposInstalacion";
import type { TipoInstalacionSeleccion } from "@/erp/orden-servicio/orden-servicio-visita/components/tipo-instalacion/useTiposInstalacion";
import type { ReinstalacionAc } from "@/erp/orden-servicio/orden-servicio-visita/components/reinstalacion-ac/ReinstalacionAcVisita";
import {
  CustomButtunsRejectConfirm,
  CustomTabPanel,
  FormTabsOnly,
  SingleIconButton,
  TabsFormBoxScene,
} from "@/shared/components/ui";
import {
  ONTOption,
  OrdenTrabajo,
} from "@/shared/interfaces";
import { useEquiposAdicionales } from "@/store/erp";
import { useCargarSeriesProductos } from "@/store/erp/productos/useCargarSeriesProductos.store";
import {
  a11yProps,
  hasPermission,
  ordenTrabajoVisitaFormSchema,
} from "@/shared/utils";
import { PermissionsEnum } from "@/auth/interfaces";
import { useUiConfirmModalStore, useUiStore } from "@/store/ui";
import RejectInstalacionAsigTecModal from "../tables/RejectInstalacionAsigTecModal";
import OTVisitaTecnicoFormTab from "./OTVisitaTecnicoFormTab";
import OTDetalleVisitaInstallTecnicoFormTab from "./OTDetalleVisitaTecnicoFormTab";
import OTMaterialesInstallTecnicoFormTab from "./OTMaterialesInstallTecnicoFormTab";
import OTDetalleClientVisitaTecnicoFormTab from "./VisitaOrdenServicioForm/OTDetalleClientVisitaTecnicoFormTab";
import dayjs from "dayjs";
import { useAuthStore } from "@/store/auth";
import {
  CustomAlertCard,
  CardPersonalizado,
} from "@/shared/components/ui/Alerts";
import { useGeolocation } from "../../hooks";
import { toast } from "react-toastify";
import { useGetProspectoByIdProspecto } from "@/shared/actions/erp";
import { UploadFotosVisitaModal } from "../tables/fotos";
import IniciarVisitaFotosModal from "../tables/fotos/IniciarVisitaFotosModal";
import FotosVisitaTab from "../tables/fotos/FotosVisitaTab";
import {
  useAvisosFotosTraslado,
  useOrdenTrabajoFotosLive,
} from "../tables/fotos/traslado/fotos-traslado.actions";
import {
  resumenFotosVisita,
  textoFotosFaltantesVisita,
} from "../tables/fotos/fotos-visita.utils";
import { CategoryProducCodeEnum } from "@/shared/interfaces/erp/inventario";

export interface SaveOrdenTrabajoVisitaTecProps {
  title: string;
  ordentrabajo: OrdenTrabajo;
  isCorrentingData?: boolean;
}

const SaveOrdenTrabajoVisitaTec: React.FC<
  SaveOrdenTrabajoVisitaTecProps
> = ({ title, ordentrabajo, isCorrentingData = false }) => {
  const navigate = useNavigate();
  //navigate
  const returnUrlInstalacionesTecnicoPage = ROUTER_PATHS.tecnico.visitasNav;

  const { handleMethodChange, valueTab, setValueTab } = useTabsFormOnly({
    initialTabValue: 1,
  });

  const { coords, permissionStatus, error, getCurrentCoords } =
    useGeolocation();
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);

  ///* local state -----------------------
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [shrink] = useState(true);
  const [openFotosModal, setFotosModal] = useState(false)
  const [openIniciarModal, setOpenIniciarModal] = useState(false)
  const [iniciarLocation, setIniciarLocation] = useState<{ lat: number; lng: number } | null>(null)

  ///* global state -----------------------
  const setConfirmDialog = useUiConfirmModalStore((s) => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    (s) => s.setConfirmDialogIsOpen,
  );
  //const setAlert = useUiStore.getState().setAlert;
  const setIsGlobalLoading = useUiStore((s) => s.setIsGlobalLoading);
  const productos_adicionales = useEquiposAdicionales((s) => s.productos);
  const setProductosAdicionales = useEquiposAdicionales((s) => s.setProductos);

  ///* form -----------------------
  const form = useForm<any>({
    resolver: yupResolver(ordenTrabajoVisitaFormSchema) as any,
    defaultValues: {
      estado: true,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = form;
 const [optionsONT, setOptionsONT] = useState<ONTOption[]>([]);
  // El prospecto solo aporta el aviso de "representante de persona con
  // discapacidad"; exige api.view_prospecto, que el técnico no tiene (toast 403
  // al abrir la visita). Solo se consulta con permiso.
  const id_pros = ordentrabajo.prospecto_data?.id_prospecto;
  const puedeVerProspecto = hasPermission(PermissionsEnum.api_view_prospecto);
  const { data: dataProspecto } = useGetProspectoByIdProspecto(
    puedeVerProspecto ? id_pros : undefined,
  );

  //
  const {
      data: dataUbicacionProd,
      isLoading: isloadingUbProducto,
      // isRefetching: isRefetchingUbProducto
    } = useFetchUbicacionProductosEnabled({
      enabled: true,
      params: {
        page_size: 1000,
        page: 1,
        categoria_producto: CategoryProducCodeEnum.EQUIPOS,
        ubicacion: ordentrabajo?.flota_data?.ubicacion,
        // filterByState: false
      },
    });

  const mensaje = [
    ordentrabajo.preventa_data.requiere_contrato_fisico ? "FISICO" : "DIGITAL",
    dataProspecto?.es_curador_tutor
      ? "Es representante de persona con discapacidad: SI"
      : null,
  ]
    .filter(Boolean)
    .join("\n");

  ///* mutations -----------------------
  const udpdateInSiteUpdateOrdenTrabajo = useUpdateOrdenTrabajo<any>({
    enableRedirect: false,
  });
  const updateordenTrabajoSinMsgMutation = useUpdateOrdenTrabajo<any>({
    enableRedirect: false,
    enableSuccessAlert: false
  })
  // "Guardar Borrador" (traído de instalaciones/prod): guarda sin cambiar el
  // estado ni finalizar y sin redirigir.
  const guardarBorradorMutation = useUpdateOrdenTrabajo<any>({
    enableRedirect: false,
    customSuccessMessage: "Borrador guardado correctamente",
  });

  const updateOrdenTrabajoMutation =
    useUpdateOrdenTrabajo<any>({
      navigate,
      returnUrl: returnUrlInstalacionesTecnicoPage,
      customMessage: "Visita finalizada correctamente",
    });

  ///* Puntos de Red -----------------------
  // El técnico ingresa puntos de red (metros de cable UTP); se persisten en el
  // agendamiento para que Activaciones los apruebe. El gate impide finalizar si
  // hay puntos de red y aún no están aprobados por Activaciones.
  const { puntoRed: puntoRedConfig, ivaPorcentaje: ivaPorcentajePR, conceptos: catalogoConceptos } =
    usePreciosConceptosVisita();
  // Conceptos/puntos/tipo se guardan con el endpoint del técnico (solo pide
  // `change_ordentrabajo`); el PUT de agendamiento pedía `change_agendamiento`
  // y al técnico le daba 403. Se envían SOLO las claves que cambia.
  const [guardandoConceptos, setGuardandoConceptos] = useState(false);
  const [puntosRed, setPuntosRed] = useState<PuntoRed[]>([]);
  const puntosRedAprobado = !!(ordentrabajo as any)?.agendamiento_data?.observaciones
    ?.puntos_red_aprobado;

  // "Otros conceptos" (definidos por ventas/activaciones). El técnico solo asigna
  // la SERIE de cada equipo (tab MATERIALES); esa serie se usa para el egreso de
  // inventario al finalizar. Se persisten en el agendamiento.
  const ubicacionFlotaTec = (ordentrabajo as any)?.flota_data?.ubicacion;
  const [conceptosVisita, setConceptosVisita] = useState<ConceptoVisita[]>([]);
  const { tipos: tiposInstalacion } = useTiposInstalacion();
  const [tipoInstalacion, setTipoInstalacion] = useState<TipoInstalacionSeleccion | null>(null);
  const [reinstalacionAc, setReinstalacionAc] = useState<ReinstalacionAc | null>(null);

  // Subida de fotos de evidencia (equipos, puntos de red, tipo de instalación).
  // Devuelve {url, fecha}; el técnico las sube a MinIO. Se guardan dentro del
  // ítem correspondiente en el agendamiento (con el guardado normal).
  const { handleUploadFile } = useUploadFile();
  const subirFotoEvidencia = async (file: File) => {
    const r = await handleUploadFile(
      file,
      `${BucketAwtENUM.IMAGE_INSTALACION}/${ordentrabajo?.id_orden_trabajo}`,
    );
    return { url: r.url, fecha: new Date().toISOString() };
  };

  // Dirty tracking: los botones "Guardar" solo se activan si hay cambios sin
  // guardar respecto de lo persistido en el agendamiento (se refresca al guardar).
  const _obsOrig = (ordentrabajo as any)?.agendamiento_data?.observaciones || {};
  const _origConceptos = Array.isArray(_obsOrig?.otros_conceptos) ? _obsOrig.otros_conceptos : [];
  const _origPuntos = Array.isArray(_obsOrig?.puntos_red) ? _obsOrig.puntos_red : [];
  const conceptosDirty = JSON.stringify(_origConceptos) !== JSON.stringify(conceptosVisita);
  const puntosRedDirty = JSON.stringify(_origPuntos) !== JSON.stringify(puntosRed);

  // Recarga puntos de red / conceptos desde el agendamiento cada vez que cambian
  // en el servidor (p.ej. al dar "Actualizar" tras que Activaciones los edite),
  // no solo al montar. Depende de las OBSERVACIONES (no del id_orden_trabajo).
  const _obsSyncKey = JSON.stringify({
    pr: (ordentrabajo as any)?.agendamiento_data?.observaciones?.puntos_red || [],
    oc: (ordentrabajo as any)?.agendamiento_data?.observaciones?.otros_conceptos || [],
    ti: (ordentrabajo as any)?.agendamiento_data?.observaciones?.tipo_instalacion || null,
    ra: (ordentrabajo as any)?.agendamiento_data?.observaciones?.reinstalacion_ac || null,
  });
  useEffect(() => {
    const pr =
      (ordentrabajo as any)?.agendamiento_data?.observaciones?.puntos_red || [];
    setPuntosRed(Array.isArray(pr) ? pr : []);
    const oc =
      (ordentrabajo as any)?.agendamiento_data?.observaciones?.otros_conceptos || [];
    setConceptosVisita(Array.isArray(oc) ? oc : []);
    setTipoInstalacion(
      (ordentrabajo as any)?.agendamiento_data?.observaciones?.tipo_instalacion || null,
    );
    setReinstalacionAc(
      (ordentrabajo as any)?.agendamiento_data?.observaciones?.reinstalacion_ac || null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_obsSyncKey]);

  // Persiste las series asignadas por el técnico a "Otros conceptos".
  const guardarSeriesConceptos = async () => {
    if (!ordentrabajo?.id_orden_trabajo) {
      toast.error("No se pudo resolver la orden de trabajo para guardar las series.");
      return;
    }
    const currentObs =
      (ordentrabajo as any)?.agendamiento_data?.observaciones || {};
    setGuardandoConceptos(true);
    try {
      await guardarObservacionesTecnico(ordentrabajo.id_orden_trabajo, {
        otros_conceptos: conceptosVisita,
        reinstalacion_ac: reinstalacionAc || null,
        tipo_instalacion: tipoInstalacion || null,
        tipo_instalacion_historial: trazaTipoInstalacion(
          currentObs?.tipo_instalacion,
          tipoInstalacion,
          currentObs?.tipo_instalacion_historial,
          currentUser?.username || "—",
          "tecnico",
        ),
      });
      toast.success("Series de equipos guardadas.");
      await refreshData();
    } catch (e) {
      console.error("No se pudo guardar las series de conceptos", e);
      toast.error("No se pudo guardar las series de equipos.");
    } finally {
      setGuardandoConceptos(false);
    }
  };

  // Detección de CABLE UTP en los materiales de la visita. La fuente real es el
  // store `useCargarSeriesProductos` (donde el modal agrega los materiales), no el
  // form field. Se combina con el form por si viene precargado. Código PDR.017* o
  // descripción contiene "CABLE UTP".
  const materialesStore = useCargarSeriesProductos((s) => s.productos);
  const materialesForm = useWatch({
    control: form.control,
    name: "materiales_utilizados",
  });
  const materialesParaCableUtp = [
    ...(Array.isArray(materialesStore) ? materialesStore : []),
    ...(Array.isArray(materialesForm) ? materialesForm : []),
  ];
  const tieneCableUtp = materialesParaCableUtp.some((m: any) => {
    const cod = (m?.codigo || m?.producto_data?.codigo || "").toUpperCase();
    const desc = (m?.descripcion || m?.producto_data?.descripcion || "").toUpperCase();
    return cod.startsWith("PDR.017") || desc.includes("CABLE UTP");
  });

  const guardarPuntosRed = async () => {
    if (!ordentrabajo?.id_orden_trabajo) {
      toast.error("No se pudo resolver la orden de trabajo para guardar los puntos de red.");
      return;
    }
    if (puntosRed.length > 0 && !tieneCableUtp) {
      toast.error('Selecciona CABLE UTP en Materiales antes de guardar los puntos de red.');
      return;
    }
    const currentObs =
      (ordentrabajo as any)?.agendamiento_data?.observaciones || {};
    const previos = Array.isArray(currentObs?.puntos_red)
      ? currentObs.puntos_red
      : [];
    // Comparar SIN las fotos: agregar fotos de evidencia no debe invalidar la
    // aprobación de Activaciones (que aprueba metros/cobro, no las fotos).
    const _sinFotos = (arr: any[]) =>
      (Array.isArray(arr) ? arr : []).map(({ foto_punto, foto_utp, ...rest }: any) => rest);
    const cambiaron =
      JSON.stringify(_sinFotos(previos)) !== JSON.stringify(_sinFotos(puntosRed));
    const histPrevio = Array.isArray(currentObs?.puntos_red_historial)
      ? currentObs.puntos_red_historial
      : [];
    const historial = cambiaron
      ? [
          ...histPrevio,
          {
            usuario: currentUser?.username || "—",
            fecha: new Date().toISOString(),
            origen: "tecnico",
            anterior: previos,
            nuevo: puntosRed,
          },
        ]
      : histPrevio;
    setGuardandoConceptos(true);
    try {
      await guardarObservacionesTecnico(ordentrabajo.id_orden_trabajo, {
        puntos_red: puntosRed,
        puntos_red_historial: historial,
        // Cualquier cambio del técnico invalida la aprobación previa.
        puntos_red_aprobado: cambiaron ? false : currentObs?.puntos_red_aprobado || false,
      });
      toast.success("Puntos de red guardados. Quedan pendientes de aprobación por Activaciones.");
      await refreshData();
    } catch (e) {
      console.error("No se pudo guardar los puntos de red", e);
      toast.error("No se pudo guardar los puntos de red.");
    } finally {
      setGuardandoConceptos(false);
    }
  };

  useEffect(() => {
    setValueTab(1);
  }, [ordentrabajo?.id_orden_trabajo]);

  ///* handlers ============================================
  const onSave = async (data: any) => {
    if (!isValid) return;

    // Persistir las series asignadas por el técnico a "Otros conceptos" ANTES de
    // finalizar, para que el egreso de inventario (backend, al REALIZADA) use la
    // serie correcta. Best-effort: no frena la finalización.
    try {
      if (ordentrabajo?.id_orden_trabajo) {
        const currentObs =
          (ordentrabajo as any)?.agendamiento_data?.observaciones || {};
        await guardarObservacionesTecnico(ordentrabajo.id_orden_trabajo, {
          otros_conceptos: conceptosVisita,
          // Persistir puntos de red (con sus fotos) preservando la aprobación
          // de Activaciones (las fotos del técnico no invalidan el cobro).
          puntos_red: puntosRed,
          reinstalacion_ac: reinstalacionAc || currentObs?.reinstalacion_ac || null,
          tipo_instalacion: tipoInstalacion || currentObs?.tipo_instalacion || null,
          tipo_instalacion_historial: trazaTipoInstalacion(
            currentObs?.tipo_instalacion,
            tipoInstalacion,
            currentObs?.tipo_instalacion_historial,
            currentUser?.username || "—",
            "tecnico",
          ),
        });
      }
    } catch (e) {
      console.error("No se pudo persistir las series/tipo al finalizar", e);
    }

    if (ordentrabajo?.coordenadas != data?.coordenadas) {
      if (ordentrabajo?.historial_cambios_coordenadas) {
        data.historial_cambios_coordenadas = [
          ...ordentrabajo?.historial_cambios_coordenadas,
          {
            usuario: currentUser?.username,
            fecha_cambio: new Date().toISOString(),
            coordenada_anterior: ordentrabajo?.coordenadas,
            coordenada_nueva: data?.coordenadas,
          },
        ];
      } else {
        data.historial_cambios_coordenadas = [
          {
            usuario: currentUser?.username,
            fecha_cambio: new Date().toISOString(),
            coordenada_anterior: ordentrabajo?.coordenadas,
            coordenada_nueva: data?.coordenadas,
          },
        ];
      }
    }

    // Si la visita NO requiere cambio de ONT no hay activacion que gestionar,
    // por eso al finalizar se marca GESTIONADA para que el registro caiga en
    // Activaciones > Gestionadas (antes desaparecia de ambas pestañas). Si SÍ
    // requiere cambio de ONT, la activacion la gestiona Activaciones (no la
    // tocamos aqui para no marcarla gestionada sin el cambio de puerto/ONT).
    const requiereCambioOntFinal =
      ordentrabajo?.requiere_cambio_ont === true ||
      data?.requiere_cambio_ont === true;

    updateOrdenTrabajoMutation.mutate({
      id: ordentrabajo?.id_orden_trabajo!,
      data: {
        estado_orden_trabajo: isCorrentingData
          ? EstadosOrdenTrabajoEnumChoice.REALIZADA_ACTUALIZADA
          : EstadosOrdenTrabajoEnumChoice.REALIZADA,
        ...(requiereCambioOntFinal
          ? {}
          : {
              estado_activacion:
                EstadosActivacionInstallOTEnumChoice.GESTIONADA,
            }),
        fecha_hora_fin_visita: dayjs().toISOString(),
        observaciones: {
          ...ordentrabajo?.observaciones,
          observacion_tecnico: data.observacion_tecnico,
          observacion_incidencia: data.observacion_incidencia,
        },
        incidencia: data.incidencia,
        prioridad_incidencia: data.prioridad_incidencia,
        historial_cambios_coordenadas:
          data?.historial_cambios_coordenadas || [],
        coordenadas: data?.coordenadas,
        materiales_equipos_adicionales: productos_adicionales,
      },
    });
  };

  const onSaveSolicitudCambioOnt = async (data: any) => {
    if (!isValid) return;
    if(data.requiere_cambio_ont && !data.motivo_cambio_ont){
      return toast.error('El motivo del cambio es obligatorio.')
    }
    const ontAnteriorData =
      data?.observaciones?.observacion_cambio_ont?.ont_actual ||
      null;
    if(data.requiere_cambio_ont && !ontAnteriorData){
      return toast.error('Seleccione la ONT actual.')
    }
    const observacionCambioOnt = data.requiere_cambio_ont
      ? {
        ...data?.observaciones?.observacion_cambio_ont,
        ont_actual: ontAnteriorData,
      }
      : null;
    //return
    try {
      await updateordenTrabajoSinMsgMutation.mutateAsync({
        id: ordentrabajo?.id_orden_trabajo!,
        data: {
          requiere_cambio_ont: data.requiere_cambio_ont,
          motivo_cambio_ont: data.motivo_cambio_ont,
          observaciones: {
            ...ordentrabajo?.observaciones,
            ...data?.observaciones,
            observacion_cambio_ont: observacionCambioOnt,
          },
          //incidencia: data.incidencia,
          //prioridad_incidencia: data.prioridad_incidencia,
          //coordenadas: data?.coordenadas,
        }
      });
      await refreshData();
    } catch (error) {
      console.error('Error al solicitar cambio de ONT', error);
    }
  }

  // ---- Guardar Borrador (visita) ----
  // Misma función que en instalaciones (prod): guarda lo que lleva el técnico
  // (observaciones, incidencia, coordenadas, equipos adicionales) SIN cambiar
  // el estado ni finalizar, y sin las validaciones de cierre. También persiste
  // conceptos/puntos de red/tipo de instalación (endpoint del técnico) para que
  // Activaciones los vea antes del cierre. Única regla: visita iniciada.
  const onSaveBorradorVisita = async () => {
    const data: any = form.getValues();
    if (
      !data?.fecha_hora_inicio_visita &&
      !(ordentrabajo as any)?.fecha_hora_inicio_visita
    ) {
      toast.error("Debe iniciar la visita para guardar el borrador.");
      return;
    }
    try {
      if (ordentrabajo?.id_orden_trabajo) {
        const currentObs =
          (ordentrabajo as any)?.agendamiento_data?.observaciones || {};
        await guardarObservacionesTecnico(ordentrabajo.id_orden_trabajo, {
          otros_conceptos: conceptosVisita,
          puntos_red: puntosRed,
          reinstalacion_ac: reinstalacionAc || currentObs?.reinstalacion_ac || null,
          tipo_instalacion: tipoInstalacion || currentObs?.tipo_instalacion || null,
          tipo_instalacion_historial: trazaTipoInstalacion(
            currentObs?.tipo_instalacion,
            tipoInstalacion,
            currentObs?.tipo_instalacion_historial,
            currentUser?.username || "—",
            "tecnico",
          ),
        });
      }
    } catch (e) {
      // Best-effort: el borrador de la OT se guarda igual.
      console.error("No se pudo persistir los conceptos en el borrador", e);
    }
    try {
      await guardarBorradorMutation.mutateAsync({
        id: ordentrabajo?.id_orden_trabajo!,
        data: {
          es_borrador: true,
          // sin estado_orden_trabajo ni fecha_hora_fin_visita: es borrador
          observaciones: {
            ...ordentrabajo?.observaciones,
            observacion_tecnico: data.observacion_tecnico,
            observacion_incidencia: data.observacion_incidencia,
          },
          incidencia: data.incidencia,
          prioridad_incidencia: data.prioridad_incidencia,
          historial_cambios_coordenadas: calcularHistorialCambiosCoordenadas({
            ordentrabajo,
            coordenadasNuevas: data?.coordenadas,
            usuario: currentUser?.username,
          }),
          coordenadas: data?.coordenadas,
          materiales_equipos_adicionales: productos_adicionales,
        },
      });
      await refreshData();
    } catch (e) {
      // El motivo ya lo mostró el cliente HTTP.
      console.error("No se pudo guardar el borrador de la visita", e);
    }
  };

  const refreshData = async () => {
    await queryClient.invalidateQueries({
      queryKey: [
        OrdenTrabajoQueryKeysEnum.OrdenTrabajo,
        ordentrabajo?.id_orden_trabajo?.toString() || "",
      ],
    });
  };

  const resetFormWithOTData = (ordenTrabajo: OrdenTrabajo) => {
    if (!ordenTrabajo?.id_orden_trabajo) return;

    const { prospecto_data, preventa_data, agendamiento_data, ...rest } =
      ordenTrabajo;
    const estado_navegacion_contrato = rest?.contrato_data?.estado_navegacion;
    const tag = rest?.contrato_data?.tag;
    delete rest?.contrato_data;

    if (ordenTrabajo?.materiales_equipos_adicionales?.length > 0) {
      setProductosAdicionales(
        (ordenTrabajo?.materiales_equipos_adicionales as any[]) || [],
      );
    }

    reset({
      ...prospecto_data,
      ...preventa_data,
      ...agendamiento_data,
      ...rest,
      estado_navegacion_contrato: estado_navegacion_contrato,
      planLabel: ordenTrabajo?.plan_data?.nombre || "",
      flotaLabel: ordenTrabajo?.flota_data?.nombre || "",
      observacion_tecnico:
        ordenTrabajo?.observaciones?.observacion_tecnico || "",
      observacion_llamada:
        agendamiento_data?.observaciones?.observacion_llamada,
      tag: tag,
    } as unknown as any);
  };

  useEffect(() => {
    if (!ordentrabajo?.id_orden_trabajo) return;
    resetFormWithOTData(ordentrabajo);
  }, [ordentrabajo]);

  useEffect(() => {
    const auxONTOptions = dataUbicacionProd?.data?.data
      ?.map((item) => {
        console.log({ itemdesupuestaont: item });
        const auxONT = item.series;
        const auxONTData: any[] = [];
        if (item.producto_data?.es_ont && auxONT?.length > 0) {
          auxONT.forEach((ont) => {
            console.log({ item });
            const auxONTSplit = ont.split("-");
            auxONTData.push({
              name: `${item.producto_data?.descripcion} - ${auxONTSplit[0]}`,
              codigo: item.producto_data?.codigo,
              cantidad: "1",
              series: [ont],
            });
          });
          return auxONTData;
        }
        return null;
      })
      ?.filter((x) => x != null);
    console.log({ auxONTOptions });
    setOptionsONT(auxONTOptions?.flat() || []);

    if (!form.getValues().ont_a_utilizar) return;

    form.reset({
      ...form.getValues(),
      ont_a_utilizar: form.getValues()?.equipos_utilizados?.[0] || {},
    });
  }, [isloadingUbProducto, dataUbicacionProd]);

  const observacionActualizacionTecnico = useWatch({
    control: form.control,
    name: "observaciones.observacion_actualizacion_tecnico",
  });
  const estadoOrdenTrabajo = useWatch({
    control: form.control,
    name: "estado_orden_trabajo",
  });
  const motivoRechazoNombre = useWatch({
    control: form.control,
    name: "motivo_rechazo_data.nombre",
  });
  const fechaSolicitadaActualizacionTecnico = useWatch({
    control: form.control,
    name: "observaciones.fecha_solicitada_actualizacion_tecnico",
  });
  const fechaHoraInicioVisita = useWatch({
    control: form.control,
    name: "fecha_hora_inicio_visita",
    defaultValue: ordentrabajo?.fecha_hora_inicio_visita || "",
  });

  const hasFechaHoraInicioVisita =
    !!fechaHoraInicioVisita &&
    String(fechaHoraInicioVisita).trim() !== "" &&
    String(fechaHoraInicioVisita).toLowerCase() !== "null" &&
    String(fechaHoraInicioVisita).toLowerCase() !== "undefined";

  // Fotos EN VIVO (sondeo): refleja al instante lo que Activaciones elimine y
  // avisa al técnico (mismo mecanismo que el traslado).
  const { ot: otFotosVisita, snapshot: snapshotFotosVisita } =
    useOrdenTrabajoFotosLive(ordentrabajo, true, "visita");
  useAvisosFotosTraslado(snapshotFotosVisita, "tecnico");
  const resumenFotosVisitaOT = resumenFotosVisita(otFotosVisita);
  // Fotos de proceso: mínimo 2 obligatorias para poder cerrar el registro (solo
  // cuentan las cargadas, no las marcas de fotos eliminadas).
  const faltanFotosProceso = resumenFotosVisitaOT.proceso < 2;
  // Fotos que Activaciones eliminó y el técnico aún no vuelve a subir.
  const fotosPorReponer = resumenFotosVisitaOT.incompleto
    ? textoFotosFaltantesVisita(resumenFotosVisitaOT)
    : "";

  return (
    <TabsFormBoxScene
      titlePage={title}
      maxWidth="xl"
      size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
      showBtns={false}
      showCustomBtns={true}
      customBtns={
        <CustomButtunsRejectConfirm
          confirmTextBtn={isCorrentingData ? "Actualizar" : "Guardar"}
          showRejectBtn={!isCorrentingData}
          rejectTextBtn={"Reportar Novedad"}
          onCancel={() => {
            setConfirmDialog({
              isOpen: true,
              title: "Abandonar Formulario",
              subtitle:
                "¿Está seguro de abandonar el formulario? Se perderán todos los cambios realizados.",
              onConfirm: () => {
                setConfirmDialogIsOpen(false);
                navigate(returnUrlInstalacionesTecnicoPage);
              },
              confirmTextBtn: "SI",
              cancelTextBtn: "NO",
            });
          }}
          onConfirm={() => {
            // Obligatorio: el tecnico debe indicar SI o NO al cambio de ONT
            // antes de finalizar. Aceptamos tanto el valor persistido en la OT
            // como el recien marcado en el form (por si el refetch va en vuelo).
            const requiereOntPersistido = ordentrabajo?.requiere_cambio_ont;
            const requiereOntForm = form.getValues()?.requiere_cambio_ont;
            const respondioCambioOnt =
              requiereOntPersistido === true ||
              requiereOntPersistido === false ||
              requiereOntForm === true ||
              requiereOntForm === false;
            if (!respondioCambioOnt) {
              toast.error(
                'Debe indicar si el cliente requiere cambio de ONT (pestaña "DETALLE VISITA") antes de finalizar la visita.',
              );
              setValueTab(2);
              return;
            }
            // Obligatorio: mínimo 2 "Fotos de proceso" antes de finalizar.
            if (faltanFotosProceso) {
              toast.error(
                'Debe subir al menos 2 "Fotos de proceso" (pestaña "FOTOS") antes de finalizar la visita.',
              );
              setValueTab(5);
              return;
            }
            // Gate: las fotos que Activaciones eliminó deben reponerse antes de finalizar.
            if (fotosPorReponer) {
              toast.error(
                `Activaciones eliminó fotos de esta visita. Vuelve a subir ${fotosPorReponer} (pestaña "FOTOS") antes de finalizar.`,
              );
              setValueTab(5);
              return;
            }
            // Gate: si hay puntos de red, debe existir CABLE UTP en Materiales.
            // (Al recargar la página los materiales del store se pierden pero los
            // puntos siguen guardados; se exige re-agregar el CABLE UTP.)
            if (puntosRed.length > 0 && !tieneCableUtp) {
              toast.error(
                'Hay puntos de red registrados pero falta el CABLE UTP en "Materiales de Visita". Vuelve a agregar el CABLE UTP (PDR.017…) antes de finalizar.',
              );
              setValueTab(4);
              return;
            }
            // Gate: si hay puntos de red, deben estar aprobados por Activaciones.
            if (puntosRed.length > 0 && !puntosRedAprobado) {
              toast.error(
                'Los puntos de red deben ser aprobados por Activaciones antes de finalizar. Guarda los puntos y espera la confirmación (usa "Actualizar" para refrescar).',
              );
              setValueTab(4);
              return;
            }
            // Gate: evidencias fotográficas obligatorias (equipos, puntos, tipo).
            const faltanFotos: string[] = [];
            for (const c of conceptosVisita) {
              if (!c?.foto_serie?.url || !c?.foto_evidencia?.url) {
                faltanFotos.push(`equipo "${c?.concepto || "—"}"`);
              }
            }
            for (const p of puntosRed) {
              if (!p?.foto_punto?.url || !p?.foto_utp?.url) {
                faltanFotos.push(`punto de red "${p?.nombre || "—"}"`);
              }
            }
            if (tipoInstalacion?.tipo && !(tipoInstalacion as any)?.foto?.url) {
              faltanFotos.push("tipo de instalación");
            }
            if (faltanFotos.length) {
              toast.error(
                `Faltan evidencias fotográficas (pestaña "MATERIALES") de: ${faltanFotos.slice(0, 5).join(", ")}${faltanFotos.length > 5 ? "…" : ""}.`,
              );
              setValueTab(4);
              return;
            }
            setFotosModal(true);
          }}
          /* onConfirm={handleSubmit(onSave, (err) => {
            return setAlert(
              true,
              `Faltan campos por llenar: ${Object.keys(err).join(", ")?.replaceAll("_", " ")?.toUpperCase()}`,
              AlertSeverityEnum.error,
            );
          })} */
          onReject={() => {
            setOpenRejectModal(true);
          }}
          showAuxiliarBtn={!isCorrentingData}
          auxiliarBtn={
            <>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={guardarBorradorMutation.isPending}
                  onClick={onSaveBorradorVisita}
                >
                  Guardar Borrador
                </Button>
              </Grid>
              <SingleIconButton
                label="Actualizar"
                color="success"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  setConfirmDialog({
                    isOpen: true,
                    title: "Refrescar Datos",
                    subtitle:
                      "Al refrescar los datos se perderán los cambios realizados. ¿Desea continuar?",
                    onConfirm: () => {
                      setConfirmDialogIsOpen(false);
                      refreshData();
                    },
                    confirmTextBtn: "SI",
                    cancelTextBtn: "NO",
                  });
                }}
              />
            </>
          }
        />
      }
      tabs={
        <>
          {observacionActualizacionTecnico &&
            estadoOrdenTrabajo ===
            EstadosOrdenTrabajoEnumChoice.ESPERA_ACTUALIZAR ? (
            <Grid container spacing={2} justifyContent={"center"} my={2}>
              <CustomAlertCard
                alertTitle="Actualización solicitada por Auditoría"
                alertMessage={`Corrección solicitada por auditoría técnica \n ${motivoRechazoNombre}: ${observacionActualizacionTecnico} (${dayjs(fechaSolicitadaActualizacionTecnico).format("DD/MM/YYYY HH:mm:ss") || "Fecha no definida"})`}
                bgColorAlert="RED"
                alertColor="error"
              />
            </Grid>
          ) : null}
          <Grid container spacing={2} justifyContent={"center"} my={1}>
            {hasFechaHoraInicioVisita ? (
              <>
                <Alert
                  icon={<AccessTimeIcon />}
                  severity="success"
                  variant="filled"
                  sx={{ width: "100%", fontWeight: 600 }}
                >
                  {`Visita iniciada a las ${dayjs(fechaHoraInicioVisita).format("HH:mm:ss")} del día ${dayjs(fechaHoraInicioVisita).format("DD/MM/YYYY")}`}
                </Alert>
                {faltanFotosProceso && (
                  <Alert
                    severity="warning"
                    variant="filled"
                    sx={{ width: "100%", fontWeight: 700, mt: 1 }}
                  >
                    No olvides subir las <b>Fotos de Proceso</b> (mín. 2) desde la
                    pestaña <b>"FOTOS"</b>, caso contrario no podrás cerrar el
                    registro.
                  </Alert>
                )}
                {!!fotosPorReponer && (
                  <Alert
                    severity="error"
                    variant="filled"
                    sx={{ width: "100%", fontWeight: 700, mt: 1 }}
                  >
                    Activaciones eliminó fotos de esta visita. Vuelve a subir{" "}
                    <b>{fotosPorReponer}</b> desde la pestaña <b>"FOTOS"</b>; sin
                    eso no podrás cerrar el registro.
                  </Alert>
                )}
                <br />
                <Grid
                  {...gridSizeMdLg12}
                  sx={{ marginTop: 2 }}
                  container
                  justifyContent={"center"}
                >
                  <CardPersonalizado
                    alertTitle="Tipo de contrato"
                    alertMessage={mensaje}
                    bgColorAlert="GRAY"
                    alertColor="error"
                  />
                </Grid>
              </>
            ) : (
              <Grid container {...gridSizeMdLg12} gap={1}>
                {(permissionStatus === "denied" ||
                  permissionStatus === "prompt") && (
                    <Grid {...gridSizeMdLg12} container justifyContent={"center"}>
                      <CustomAlertCard
                        alertTitle="Habilite el acceso a la ubicación"
                        alertMessage={
                          error ??
                          "Necesitamos acceso a tu ubicación para continuar. Actívala en tu navegador y vuelve a intentarlo."
                        }
                        bgColorAlert="RED"
                        alertColor="error"
                      />
                    </Grid>
                  )}
                {permissionStatus === "loading" && (
                  <Grid {...gridSizeMdLg12} container justifyContent={"center"}>
                    <CustomAlertCard
                      alertTitle="Habilite el acceso a la ubicación"
                      alertMessage={
                        coords?.accuracy
                          ? `Precisión actual: ${Math.round(coords.accuracy)}m — esperando señal GPS...`
                          : "Iniciando GPS, mantente en campo abierto para mejor señal."
                      }
                      alertColor="info"
                    />
                  </Grid>
                )}

                <Grid {...gridSizeMdLg12} container justifyContent={"center"}>
                  <CardPersonalizado
                    alertTitle="Tipo de contrato"
                    alertMessage={mensaje}
                    bgColorAlert="GRAY"
                    alertColor="error"
                  />
                </Grid>
                <Grid {...gridSizeMdLg12} container justifyContent={"center"}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="info"
                    onClick={async () => {
                      setIsGlobalLoading(true);
                      const location = coords ?? (await getCurrentCoords());
                      setIsGlobalLoading(false);
                      if (!location) {
                        toast.error("No se pudo obtener la ubicación actual");
                        return;
                      }
                      // Abre el modal que EXIGE Fotos de ingreso (mín 2, máx 10)
                      // antes de registrar hora de inicio + coordenadas.
                      setIniciarLocation({ lat: location.lat, lng: location.lng });
                      setOpenIniciarModal(true);
                    }}
                  >
                    Iniciar Visita
                  </Button>
                </Grid>
              </Grid>
            )}
          </Grid>
          <FormTabsOnly onChange={handleMethodChange} value={valueTab}>
            <Tab label="ORDEN TRABAJO" value={1} {...a11yProps(1)} />
            <Tab label="DETALLE VISITA" value={2} {...a11yProps(2)} />
            <Tab label="INFORMACIÓN CLIENTE" value={3} {...a11yProps(3)} />
            <Tab label="MATERIALES" value={4} {...a11yProps(4)} />
            <Tab label="FOTOS" value={5} {...a11yProps(5)} />
          </FormTabsOnly>
        </>
      }
    >
      <CustomTabPanel index={valueTab} value={1}>
        <OTVisitaTecnicoFormTab
          form={form}
          errors={errors}
          shrink={shrink}
        />
      </CustomTabPanel>
      <CustomTabPanel index={valueTab} value={2}>
        <OTDetalleVisitaInstallTecnicoFormTab
          form={form}
          errors={errors}
          ordenTrabajo={ordentrabajo}
          onSaveSolicitudCambioOnt={onSaveSolicitudCambioOnt}
          isSavingCambioOnt={updateordenTrabajoSinMsgMutation.isPending}
        />
      </CustomTabPanel>
      <CustomTabPanel index={valueTab} value={3}>
        <OTDetalleClientVisitaTecnicoFormTab
          form={form}
          errors={errors}
          shrink={shrink}
        />
      </CustomTabPanel>

      <CustomTabPanel index={valueTab} value={4}>
        <OTMaterialesInstallTecnicoFormTab
          form={form}
          ordenTrabajo={ordentrabajo}
          optionsONT={optionsONT}
          isloadingUbProducto={isloadingUbProducto}
          esVisita={true}
          puntosRed={puntosRed}
          onChangePuntosRed={setPuntosRed}
          onGuardarPuntosRed={guardarPuntosRed}
          guardandoPuntosRed={guardandoConceptos}
          puntoRedConfig={puntoRedConfig}
          ivaPorcentajePuntoRed={ivaPorcentajePR}
          tieneCableUtp={tieneCableUtp}
          puntosRedAprobado={puntosRedAprobado}
          puntosRedDirty={puntosRedDirty}
          conceptosVisita={conceptosVisita}
          onChangeConceptosVisita={setConceptosVisita}
          onGuardarSeriesConceptos={guardarSeriesConceptos}
          guardandoConceptos={guardandoConceptos}
          conceptosDirty={conceptosDirty}
          ubicacionFlotaTec={ubicacionFlotaTec}
          catalogoConceptos={catalogoConceptos}
          tipoInstalacion={tipoInstalacion}
          onChangeTipoInstalacion={setTipoInstalacion}
          tiposInstalacion={tiposInstalacion}
          reinstalacionAc={reinstalacionAc}
          onChangeReinstalacionAc={setReinstalacionAc}
          onUploadFotoEvidencia={subirFotoEvidencia}
        />
      </CustomTabPanel>

      <CustomTabPanel index={valueTab} value={5}>
        <FotosVisitaTab ordenTrabajo={ordentrabajo} onRefresh={refreshData} />
      </CustomTabPanel>

      <RejectInstalacionAsigTecModal
        open={openRejectModal}
        setOpen={setOpenRejectModal}
        idOrdenTrabajo={ordentrabajo?.id_orden_trabajo!}
        observacionesOrdenTrabajo={ordentrabajo?.observaciones}
        ordentrabajo={ordentrabajo}
      />
      <UploadFotosVisitaModal
        open={openFotosModal}
        onClose={() => { setFotosModal(false) }}
        onSuccess={() => {
          handleSubmit(onSave)();
        }}
        ordenTrabajo={ordentrabajo}
      />
      <IniciarVisitaFotosModal
        open={openIniciarModal}
        onClose={() => setOpenIniciarModal(false)}
        onSuccess={() => refreshData()}
        ordenTrabajo={ordentrabajo}
        location={iniciarLocation}
        username={currentUser?.username}
      />
    </TabsFormBoxScene>
  );
};

export default SaveOrdenTrabajoVisitaTec;
